import { emptyMods, type ModFlags } from "../../../shared/mods";
import type { CharacterRender, HatRender } from "../../../shared/shop-catalog";
import { FLAP_IMPULSE, GRAVITY, MAX_FALL_SPEED } from "../../config";
import { clamp, lerp } from "../../utils/math";
import { drawCharacterSprite } from "./CharacterSprite";
import { drawHatBack, drawHatFront } from "./HatAccessory";

export type LizardState = "idle" | "flying" | "dead";

const RADIUS = 14;
const HITBOX_RADIUS = 11;

const DEFAULT_CHAR: CharacterRender = {
  kind: "lizard",
  body: "#4caf50",
  belly: "#aed581",
  tail: "#388e3c",
  legs: "#2e7d32",
  eyeWhite: "#ffffff",
  eyePupil: "#000000",
};

const DEFAULT_HAT: HatRender = {
  kind: "propeller",
  wedges: ["#d32f2f", "#fbc02d", "#1976d2", "#388e3c"],
  blade: "#e0e0e0",
  hub: "#1a1f2b",
};

export class Lizard {
  x: number;
  y: number;
  vy = 0;
  rot = 0;
  state: LizardState = "idle";

  private prevY: number;
  private prevRot = 0;
  private idleAnchorY: number;
  private bobTime = 0;
  private flapTime = 0;
  private propellerAngle = 0;
  private charSkin: CharacterRender = DEFAULT_CHAR;
  private hatSkin: HatRender = DEFAULT_HAT;
  private mods: ModFlags = emptyMods();

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.prevY = y;
    this.idleAnchorY = y;
  }

  reset(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.prevY = y;
    this.idleAnchorY = y;
    this.vy = 0;
    this.rot = 0;
    this.prevRot = 0;
    this.state = "idle";
    this.bobTime = 0;
    this.flapTime = 0;
    this.propellerAngle = 0;
  }

  flap(): void {
    if (this.state === "dead") return;
    this.state = "flying";
    const impulse = this.mods.invertedControls ? FLAP_IMPULSE : -FLAP_IMPULSE;
    this.vy = impulse;
    if (this.mods.flapBackward) this.x = Math.max(20, this.x - 18);
    this.flapTime = 0.15;
  }

  kill(): void {
    this.state = "dead";
  }

  update(dt: number): void {
    this.prevY = this.y;
    this.prevRot = this.rot;
    this.bobTime += dt;

    // propeller spins constantly; faster shortly after a flap
    const propSpeed = this.state === "dead" ? 4 : this.flapTime > 0 ? 32 : 14;
    this.propellerAngle = (this.propellerAngle + propSpeed * dt) % (Math.PI * 2);

    if (this.state === "idle") {
      this.y = this.idleAnchorY + Math.sin(this.bobTime * 4) * 4;
      this.rot = 0;
      return;
    }

    let gravity = GRAVITY;
    if (this.mods.zeroGravity) gravity = 0;
    else if (this.mods.reverseGravity) gravity = -GRAVITY;
    const maxFall = this.mods.slipperyAir ? MAX_FALL_SPEED * 1.6 : MAX_FALL_SPEED;
    this.vy = clamp(this.vy + gravity * dt, -FLAP_IMPULSE * 1.2, maxFall);
    this.y += this.vy * dt;

    if (this.flapTime > 0) this.flapTime = Math.max(0, this.flapTime - dt);

    const targetRot = clamp(this.vy / 600, -0.5, 1.2);
    this.rot = lerp(this.rot, targetRot, this.vy < 0 ? 0.4 : 0.1);
  }

  hitboxRadius(): number {
    return this.mods.brokenHitbox ? HITBOX_RADIUS * 2.8 : HITBOX_RADIUS;
  }

  setSkin(char: CharacterRender | null, hat: HatRender | null): void {
    this.charSkin = char ?? DEFAULT_CHAR;
    this.hatSkin = hat ?? DEFAULT_HAT;
  }

  setMods(flags: ModFlags): void {
    this.mods = flags;
  }

  render(ctx: CanvasRenderingContext2D, alpha: number): void {
    const drawY = lerp(this.prevY, this.y, alpha);
    const drawRot = lerp(this.prevRot, this.rot, alpha);

    ctx.save();
    ctx.translate(this.x, drawY);
    ctx.rotate(drawRot);
    const scaleX = (this.mods.giantSprite ? 2 : 1) * (this.mods.longNeck ? 2 : 1);
    const scaleY = this.mods.giantSprite ? 2 : 1;
    if (scaleX !== 1 || scaleY !== 1) ctx.scale(scaleX, scaleY);

    const r = RADIUS;
    const hatAnim = {
      propAngle: this.propellerAngle,
      bobTime: this.bobTime,
      flapTime: this.flapTime,
    };
    drawHatBack(ctx, r, this.hatSkin, hatAnim);
    drawCharacterSprite(ctx, r, this.effectiveSkin(), {
      bobTime: this.bobTime,
      flapTime: this.flapTime,
    });
    drawHatFront(ctx, r, this.hatSkin, hatAnim);

    ctx.restore();
  }

  private effectiveSkin(): CharacterRender {
    if (this.mods.rainbowLizard) {
      const t = performance.now() / 200;
      const hue = (t % 360);
      return {
        kind: "lizard",
        body: `hsl(${hue},85%,55%)`,
        belly: `hsl(${(hue + 30) % 360},85%,75%)`,
        tail: `hsl(${(hue + 60) % 360},85%,40%)`,
        legs: `hsl(${(hue + 90) % 360},85%,30%)`,
      };
    }
    if (this.mods.glitchedTextures) {
      const rand = (off: number): string =>
        `hsl(${Math.floor(Math.random() * 360 + off) % 360},${60 + Math.random() * 40}%,${30 + Math.random() * 50}%)`;
      return {
        kind: "lizard",
        body: rand(0),
        belly: rand(40),
        tail: rand(80),
        legs: rand(120),
      };
    }
    return this.charSkin;
  }
}

